import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaHeart, FaShare, FaWhatsapp, FaPhone } from 'react-icons/fa';
import { useDispatch } from 'react-redux';
import { addToWishlist } from '../redux/slices/uiSlice';
import toast from 'react-hot-toast';
import { productService } from '../services/productService';
import ProductCard from '../components/products/ProductCard.jsx';
import Loader from '../components/common/Loader.jsx';

const ProductDetailPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedImage, setSelectedImage] = useState(0);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await productService.getProductById(id);
        setProduct(response.data);
        
        // Fetch related products
        const relatedResponse = await productService.getProducts({
          category: response.data.category?._id,
          limit: 4,
        });
        setRelatedProducts(relatedResponse.data.filter(p => p._id !== id));
      } catch (error) {
        console.error('Error fetching product:', error);
        toast.error('Failed to load product');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleWishlist = () => {
    dispatch(addToWishlist(product._id));
    toast.success('Added to wishlist!');
  };

  if (loading) {
    return <Loader />;
  }

  if (!product) {
    return (
      <div className="container-custom py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Product not found</h2>
        <Link to="/products" className="btn-primary inline-block">
          Back to Products
        </Link>
      </div>
    );
  }

  const primaryImage = product.images?.[selectedImage];

  return (
    <div className="py-8">
      <div className="container-custom">
        {/* Breadcrumb */}
        <Link to="/products" className="inline-flex items-center gap-2 text-gray-600 hover:text-primary-600 mb-6">
          <FaArrowLeft /> Back to Products
        </Link>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Images */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-4"
          >
            <div className="bg-gray-100 dark:bg-gray-800 rounded-2xl overflow-hidden">
              <img
                src={primaryImage?.url || 'https://via.placeholder.com/600x600'}
                alt={product.name}
                className="w-full h-96 object-cover"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex gap-4 overflow-x-auto">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      selectedImage === index ? 'border-primary-600' : 'border-transparent'
                    }`}
                  >
                    <img src={img.url} alt={`${product.name} ${index}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>

          {/* Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="space-y-6"
          >
            <div>
              <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                {product.brand?.name}
              </div>
              <h1 className="text-3xl font-bold mb-4">{product.name}</h1>
              <div className="flex items-center gap-4">
                <span className="text-xl font-bold text-primary-600">
                  RS:{product.pricing?.salePrice?.toLocaleString()}
                </span>
                {product.pricing?.originalPrice > product.pricing?.salePrice && (
                  <span className="text-lg text-gray-500 line-through">
                    RS:{product.pricing.originalPrice.toLocaleString()}
                  </span>
                )}
              </div>
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              <span className={`badge ${
                product.inventory?.stock > 0
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
              }`}>
                {product.inventory?.stock > 0 ? 'In Stock' : 'Out of Stock'}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {product.inventory?.stock} items available
              </span>
            </div>

            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-gray-600 dark:text-gray-400">{product.description}</p>
            </div>

            {/* Specifications */}
            {product.specifications && (
              <div>
                <h3 className="font-semibold mb-3">Specifications</h3>
                <div className="grid grid-cols-2 gap-4">
                  {product.specifications.ram && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">RAM</div>
                      <div className="font-semibold">{product.specifications.ram}</div>
                    </div>
                  )}
                  {product.specifications.storage && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Storage</div>
                      <div className="font-semibold">{product.specifications.storage}</div>
                    </div>
                  )}
                  {product.specifications.display && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Display</div>
                      <div className="font-semibold">{product.specifications.display}</div>
                    </div>
                  )}
                  {product.specifications.processor && (
                    <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-lg">
                      <div className="text-sm text-gray-500">Processor</div>
                      <div className="font-semibold">{product.specifications.processor}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-4">
              <button
                onClick={handleWishlist}
                className="flex-1 btn-secondary flex items-center justify-center gap-2"
              >
                <FaHeart /> Add to Wishlist
              </button>
              <a
                href={`https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=I'm interested in ${encodeURIComponent(product.name)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-green-500 text-white px-6 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-green-600 transition-all"
              >
                <FaWhatsapp /> WhatsApp
              </a>
              <a
                href={`tel:${process.env.REACT_APP_PHONE_NUMBER}`}
                className="flex-1 btn-primary flex items-center justify-center gap-2"
              >
                <FaPhone /> Call Now
              </a>
            </div>

            {/* Warranty */}
            {product.warranty && (
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="font-semibold text-blue-800 dark:text-blue-300 mb-1">
                  Warranty Information
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400">
                  {product.warranty.type} - {product.warranty.duration}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="section-title mb-8">Related Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct._id} product={relatedProduct} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetailPage;
